import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { AppButton, Reveal, WhatsAppButton } from '@/components/atoms';
import { Section } from '@/components/organisms';
import { introVehicles } from '@/config/introVehicles';
import { type Service } from '@/content/services';

import { OVERVIEW_TITLE_ID } from '../content';
import { ServiceFactList, ServiceInquiryButton } from '../molecules';

type Props = { service: Service; pillarTitle: string };

/**
 * What this service is, and the few things about it worth knowing without
 * reading.
 *
 * The prose leads and the facts sit beside it, which is the right way round
 * for a page somebody has arrived at from a menu: they came for "what is
 * this", and the aside is for the reader who came to check one thing and
 * leave. The aside is sticky, so that reader keeps the figures — and the way
 * to reach us — in view while the prose scrolls.
 *
 * The heading is the pillar's name rather than the service's. The service's
 * name is the page's h1, already in the band above, and repeating it here
 * would be the second of two identical headings a scroll apart; the pillar
 * says the one thing the h1 does not, which is where this service sits.
 *
 * The outcome opens the aside, above the facts, and it is the only line on
 * the page set as a result rather than as a description. A reader who came
 * to check one thing and leave is reading this column and not the prose
 * beside it, so the column has to answer "what do I walk away with" before
 * it answers anything else — the facts underneath are the qualifiers on that
 * answer, which is the right order and was not the order before.
 */
export function ServiceOverviewBand({ service, pillarTitle }: Props) {
  return (
    <Section
      labelledBy={OVERVIEW_TITLE_ID}
      backdrop={{ picture: introVehicles['land-cruiser'].image, placement: 'left' }}
    >
      <Grid container spacing={{ xs: 10, md: 12 }}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Stack spacing={5}>
            <Typography variant="overline" sx={{ color: 'primary.main' }}>
              {pillarTitle}
            </Typography>
            <Typography id={OVERVIEW_TITLE_ID} variant="h2" component="h2">
              What this service is
            </Typography>
            {service.overview.map((paragraph) => (
              <Typography
                key={paragraph}
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: '68ch' }}
              >
                {paragraph}
              </Typography>
            ))}
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Reveal direction="right">
            {/* Sticky, which needs this column not to be stretched by the row
                — see the sticky-column note; `alignItems` is unset on purpose. */}
            <Stack
              spacing={5}
              sx={{
                position: { md: 'sticky' },
                top: { md: 120 },
                p: { xs: 5, md: 6 },
                borderRadius: 3,
                border: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant="h6" component="h3">
                At a glance
              </Typography>

              <Stack
                spacing={3}
                sx={{
                  p: 4,
                  borderRadius: 2,
                  bgcolor: 'rgba(var(--mui-palette-primary-mainChannel) / 0.07)',
                  borderLeft: 3,
                  borderColor: 'primary.main',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    fontWeight: 700,
                    // `text.disabled` fails AA at this size; quiet, not unreadable.
                    color: 'text.secondary',
                  }}
                >
                  What you end up with
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.6 }}>
                  {service.outcome}
                </Typography>
              </Stack>

              <ServiceFactList facts={service.facts} />

              <Stack spacing={3}>
                {/* The service's own tool, where it has one — the reader who
                    would rather work it out than ask. Not every service does,
                    so the email and WhatsApp actions are always here. */}
                {service.tool && (
                  <AppButton to={service.tool.to} appVariant="secondary">
                    {service.tool.label}
                  </AppButton>
                )}
                <ServiceInquiryButton serviceTitle={service.title} />
                <WhatsAppButton message={service.whatsappMessage}>
                  Ask about this service
                </WhatsAppButton>
              </Stack>
            </Stack>
          </Reveal>
        </Grid>
      </Grid>
    </Section>
  );
}
